import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { fetchMemberDashboard } from "../api/fetchMemberDashboard/fetchMemberDashboardApi";
import { MemberDashboard } from "../api/fetchMemberDashboard/types";
import { syncMemberDashboard } from "../api/syncMemberDashboard/syncMemberDashboardApi";
import { KPI_CONFIG, SECTIONS, SectionCode } from "../config/kpiConfig";
import { DashboardKPI, DashboardSection } from "../types";
import { toDashboardKPI } from "../utils/toDashboardKPI";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-07` → `Jul 2026`. */
const longMonth = (yearMonth: string): string =>
  `${MONTH_LABELS[Number(yearMonth.slice(5, 7)) - 1] ?? yearMonth} ${yearMonth.slice(0, 4)}`;

/** What the API said when it refused the request, or a plain fallback. */
const requestProblem = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.errors?.detail;

    if (typeof detail === "string" && detail !== "") {
      return detail;
    }
  }

  return "Your numbers could not be loaded. Please try again.";
};

/**
 * Page state for the member KPI dashboard.
 *
 * The KPIs the API returns are paired with their entry in `config/kpiConfig.ts`;
 * anything without one is skipped, so a newly-calculated KPI cannot reach the
 * page as a card with blank prose.
 *
 * That config also decides which section a card belongs to, and a section with
 * no cards does not render — so a section declared ahead of its calculations
 * leaves no empty heading behind.
 *
 * **There is no period to choose, and nothing asks for one.** The page used to
 * carry `?period=` in the URL and a picker in the header, either of which
 * could change the window and re-fetch. Both are gone: the request sends no
 * period at all and the API answers with its own default, the last completed
 * month and the twelve before it. `periodStart`/`periodEnd` off the response
 * are the only statement of which months these are.
 */
export const useMemberDashboardPage = () => {
  const [dashboard, setDashboard] = useState<MemberDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [isResyncing, setIsResyncing] = useState(false);

  /**
   * Rebuild the member's figures, then show them.
   *
   * Two requests rather than one: the rebuild writes, and the dashboard is read
   * back through the same endpoint the page already uses, so there is one
   * definition of what a card shows rather than a second shape to keep in step.
   *
   * No error *reporting*: a failure is toasted by the axios interceptor, the
   * refetch never runs because the chain is broken, and the page keeps showing
   * the figures it already had — which are still true, just not newer.
   *
   * The empty `catch` is not error handling, it is rejection hygiene. Without
   * it the interceptor's re-thrown rejection is unhandled, which fails the test
   * run and logs an uncaught error in the browser for a failure the member has
   * already been told about.
   *
   * Clears `loadProblem` on success: a member whose first load failed and who
   * then re-syncs successfully should not be left reading a stale error.
   */
  const resync = () => {
    setIsResyncing(true);

    syncMemberDashboard()
      .then(() => fetchMemberDashboard())
      .then((response) => {
        setDashboard(response.data);
        setLoadProblem(null);
      })
      .catch(() => undefined)
      .finally(() => setIsResyncing(false));
  };

  useEffect(() => {
    const controller = new AbortController();

    fetchMemberDashboard(controller.signal)
      .then((response) => setDashboard(response.data))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoadProblem(requestProblem(error));
        }
      })
      .finally(() => {
        // Aborted means the component went away — under StrictMode's double
        // mount, before the response it asked for landed. Clearing the flag
        // there would settle the page on behalf of a render that is gone.
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  const sections: DashboardSection[] = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    const kpisBySection = new Map<SectionCode, DashboardKPI[]>();

    for (const kpi of dashboard.kpis) {
      const config = KPI_CONFIG[kpi.code];

      if (!config) {
        continue;
      }

      const existing = kpisBySection.get(config.section) ?? [];
      kpisBySection.set(config.section, [
        ...existing,
        toDashboardKPI(kpi, config, dashboard.periodDays),
      ]);
    }

    // Section order comes from SECTIONS, card order from the API within each.
    return (Object.keys(SECTIONS) as SectionCode[])
      .filter((code) => (kpisBySection.get(code) ?? []).length > 0)
      .map((code) => ({
        code,
        ...SECTIONS[code],
        kpis: kpisBySection.get(code) ?? [],
      }));
  }, [dashboard]);

  /**
   * Scroll to the card named in the URL hash, once there is a card to scroll to.
   *
   * The browser's own hash jump fires on navigation, which is before the first
   * response lands, so it finds nothing and silently does nothing — the member
   * arrives at the top of the page with no sign the link missed. Re-running it
   * against `sections` is what makes an inbound link from the account mapping
   * page work at all.
   *
   * Once only per hash: a member who has scrolled away while changing the period
   * should not be yanked back.
   */
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "" || sections.length === 0) {
      return;
    }

    document
      .getElementById(hash.slice(1))
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, sections.length]);

  const periodLabel = dashboard
    ? `${longMonth(dashboard.periodStart)} – ${longMonth(dashboard.periodEnd)}`
    : null;

  return { sections, periodLabel, isLoading, loadProblem, isResyncing, resync };
};
